import { defineStore } from 'pinia';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import * as yup from 'yup';

import { i18n } from '@/locales';

import { useSubscriptionStore } from '@/stores/index';

import type { OtherEquipment } from '@/types/subscription.type';

import {
  EEquipmentInstallationStatus,
  EMainHeatingSource,
  EWaterHeatingSource,
} from '@/constants/subscription.constants';

export default defineStore('equipments-declaration', () => {
  const t = i18n.global.t;

  const subscriptionStore = useSubscriptionStore();

  const installationEquipmentsSchema = yup.object({
    hasSolarPanels: yup.string().required(t('common.forms.required')),
    hasRobinDevice: yup.string().required(t('common.forms.required')),
    hasPhysicalBattery: yup.string().required(t('common.forms.required')),
    hasMyLight150ElectricVehicleCharger: yup.string().required(t('common.forms.required')),
    mainHeatingSource: yup.string().required(t('common.forms.required')),
    waterHeatingSource: yup.string().required(t('common.forms.required')),
  });

  const {
    errors,
    defineField,
    validate: validateInstallationEquipments,
  } = useForm({
    validationSchema: installationEquipmentsSchema,
  });

  const [hasSolarPanels] = defineField('hasSolarPanels');
  const [hasRobinDevice] = defineField('hasRobinDevice');
  const [hasPhysicalBattery] = defineField('hasPhysicalBattery');
  const [hasMyLight150ElectricVehicleCharger] = defineField('hasMyLight150ElectricVehicleCharger');
  const [mainHeatingSource] = defineField('mainHeatingSource');
  const [waterHeatingSource] = defineField('waterHeatingSource');

  const otherEquipments = ref<OtherEquipment[]>([]);

  const hasSolarPanelsInstalled = computed(() =>
    hasSolarPanels.value
      ? hasSolarPanels.value === EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
        hasSolarPanels.value === EEquipmentInstallationStatus.ALREADY_EQUIPPED
      : true
  );

  const hasRobinDeviceInstalled = computed(() =>
    hasRobinDevice.value
      ? hasRobinDevice.value === EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
        hasRobinDevice.value === EEquipmentInstallationStatus.ALREADY_EQUIPPED
      : true
  );

  const hasAirToWaterHeatPump = computed(() =>
    mainHeatingSource ? mainHeatingSource.value === EMainHeatingSource.AirToWaterHeatPump : true
  );

  const hasElectricWaterHeater = computed(() =>
    waterHeatingSource ? waterHeatingSource.value === EWaterHeatingSource.ElectricWaterHeater : true
  );

  const hasElectricVehicleCharger = computed(() =>
    hasMyLight150ElectricVehicleCharger.value
      ? hasMyLight150ElectricVehicleCharger.value ===
          EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
        hasMyLight150ElectricVehicleCharger.value === EEquipmentInstallationStatus.ALREADY_EQUIPPED
      : true
  );

  const hasSolarPanelsWithRobinDevice = computed(
    () => hasSolarPanelsInstalled.value && hasRobinDeviceInstalled.value
  );

  const hasSolarPanelsWithoutRobinDevice = computed(
    () =>
      hasSolarPanelsInstalled.value &&
      hasRobinDevice.value === EEquipmentInstallationStatus.NOT_EQUIPPED
  );

  const hasRobinDeviceWithoutSolarPanels = computed(
    () =>
      hasSolarPanels.value === EEquipmentInstallationStatus.NOT_EQUIPPED &&
      hasRobinDeviceInstalled.value
  );

  const hasNoSolarPanelsAndNoRobinDevice = computed(
    () =>
      hasSolarPanels.value === EEquipmentInstallationStatus.NOT_EQUIPPED &&
      hasRobinDevice.value === EEquipmentInstallationStatus.NOT_EQUIPPED
  );

  const hasCompatibleEquipments = computed(() => {
    return (
      hasAirToWaterHeatPump.value || hasElectricWaterHeater.value || hasElectricVehicleCharger.value
    );
  });

  const setDefautValues = () => {
    const { getCurrentSubscriptionProfile } = subscriptionStore;

    hasSolarPanels.value = getCurrentSubscriptionProfile?.hasSolarPanels || '';

    hasRobinDevice.value = getCurrentSubscriptionProfile?.hasRobinDevice || '';

    hasPhysicalBattery.value = getCurrentSubscriptionProfile?.hasPhysicalBattery || '';

    hasMyLight150ElectricVehicleCharger.value =
      getCurrentSubscriptionProfile?.hasMyLight150ElectricVehicleCharger || '';

    mainHeatingSource.value = getCurrentSubscriptionProfile?.mainHeatingSource || '';

    waterHeatingSource.value = getCurrentSubscriptionProfile?.waterHeatingSource || '';

    otherEquipments.value = getCurrentSubscriptionProfile?.otherEquipments || [];
  };

  return {
    hasSolarPanels,
    hasRobinDevice,
    hasPhysicalBattery,
    hasMyLight150ElectricVehicleCharger,
    mainHeatingSource,
    waterHeatingSource,
    otherEquipments,

    errors,

    hasNoSolarPanelsAndNoRobinDevice,
    hasSolarPanelsInstalled,
    hasRobinDeviceInstalled,
    hasAirToWaterHeatPump,
    hasElectricWaterHeater,
    hasElectricVehicleCharger,
    hasSolarPanelsWithRobinDevice,
    hasSolarPanelsWithoutRobinDevice,
    hasRobinDeviceWithoutSolarPanels,
    hasCompatibleEquipments,

    validateInstallationEquipments,
    setDefautValues,
  };
});
